// CEN26oct: resúmenes en la lista; el editor recibe SIEMPRE el detalle fresco.
// Instancia por pantalla/identidad. Sin caché global ni cambios de datos.
export function budgetSummary(budget, fromDetail = false) {
  const { items, revisions, attachments, history, ...summary } = budget;
  const count = (value, fallback) => Number.isFinite(value) && value >= 0 ? value : fallback;
  const itemCount = Array.isArray(items) ? items.filter(item => !item.is_group).length : 0;
  summary.items_count = fromDetail ? itemCount : count(summary.items_count, itemCount);
  summary.attachments_count = summary.attachments_count ?? (attachments || []).length;
  // El popover sólo consume estos cinco metadatos, nunca snapshots de ítems.
  summary.history = Array.isArray(history) ? history.map(event => ({
    status: event?.status, label: event?.label, timestamp: event?.timestamp,
    user_name: event?.user_name, comment: event?.comment,
  })) : history;
  summary._list_light = true;
  return summary;
}

export function budgetReadCancelled(error) {
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED" || error?.__CANCEL__ === true;
}

function cancelled() {
  const error = new Error("Consulta cancelada");
  error.name = "AbortError";
  return error;
}

export function createBudgetReads(get, api, options) {
  let listController, statsController, detailController, referenceController;
  let referencePromise = null;

  async function read(kind, path, params) {
    const controller = new AbortController();
    let previous;
    if (kind === "list") { previous = listController; listController = controller; }
    if (kind === "stats") { previous = statsController; statsController = controller; }
    if (kind === "detail") { previous = detailController; detailController = controller; }
    previous?.abort();
    try {
      const response = await get(`${api}${path}`, { ...options, params, signal: controller.signal });
      if (controller.signal.aborted) throw cancelled();
      return response.data;
    } finally {
      if (kind === "list" && listController === controller) listController = null;
      if (kind === "stats" && statsController === controller) statsController = null;
      if (kind === "detail" && detailController === controller) detailController = null;
    }
  }

  return {
    async list(params) {
      const rows = await read("list", "/budgets", { ...params, light: true });
      if (!Array.isArray(rows)) throw new Error("No se recibió la lista de presupuestos");
      return rows.map(row => budgetSummary(row));
    },
    stats() { return read("stats", "/budgets/stats/summary", { light: true }); },
    async detail(budget) {
      const id = budget?._id || budget?.id;
      if (!id) throw new Error("No se pudo identificar el presupuesto");
      const data = await read("detail", `/budgets/${encodeURIComponent(id)}`);
      if (!data || String(data._id || data.id) !== String(id) || !Array.isArray(data.items) || data._list_light) {
        throw new Error("No se recibió el presupuesto completo; no se abrirá el editor");
      }
      return data;
    },
    references(force = false) {
      if (referencePromise && !force) return referencePromise;
      referenceController?.abort();
      const controller = new AbortController();
      referenceController = controller;
      const config = { ...options, signal: controller.signal };
      const task = Promise.all([
        get(`${api}/projects`, config),
        get(`${api}/listas/clientes`, config).catch(error => {
          if (controller.signal.aborted) throw cancelled();
          return { data: [] }; // Mantiene el fallback legacy de este catálogo.
        }),
        get(`${api}/listas/obras`, config).catch(error => {
          if (controller.signal.aborted) throw cancelled();
          return { data: [] };
        }),
      ]).then(([projects, clientes, obras]) => {
        if (controller.signal.aborted) throw cancelled();
        return {
          projects: Array.isArray(projects.data) ? projects.data : [],
          clientes: Array.isArray(clientes.data) ? clientes.data : [],
          obras: Array.isArray(obras.data) ? obras.data : [],
        };
      });
      referencePromise = task;
      task.catch(() => { if (referencePromise === task) referencePromise = null; });
      return task;
    },
    cancelDetail() { detailController?.abort(); detailController = null; },
    cancelAll() {
      [listController, statsController, detailController, referenceController].forEach(controller => controller?.abort());
      referencePromise = null;
    },
  };
}
